import { Col, Form, Row, Select } from "antd";
import Table from "atoms/Table";
import Title from "atoms/Title";
import queryKeys from "common/queryKey";
import { useScrollFetch } from "hooks/useScrollFetch";
import moment from "moment";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setFilter } from "redux/actions";
import {
    getClientid,
    getUserInfo,
    getUserList,
} from "redux/selectors";
import { ButtonOutlined } from "reusableComponent/UIButtons";
import styled from "styled-components";
import { utils, writeFileXLSX } from "xlsx";

function OnboardingReport() {
    const clientId = useSelector(getClientid);
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState(0);
    const [selectedManagingOffice, setSelectedManagingOffice] = useState("Kenya");

    const columns = [
        {
            title: "Name",
            dataIndex: "fullname",
            key: "fullname",
            className: "normal-column",
            render: (val) => <span>{val}</span>,
        },
        {
            title: "Email Id",
            dataIndex: "userid",
            key: "userid",
            className: "normal-column",
            render: (val) => <span>{val ?? "-"}</span>,
        },
        {
            title: "Designation",
            dataIndex: "designation",
            key: "designation",
            className: "normal-column",
            ellipsis: { showTitle: true },
        },
        {
            title: "Submission Status",
            dataIndex: "onBoardingFormStatus",
            key: "onBoardingFormStatus",
            className: "normal-column",
            render: (val) => <span>{val === "0" ? "Yes" : "No"}</span>,
        },
        {
            title: "Country",
            dataIndex: "managingoffice",
            key: "managingoffice",
            className: "normal-column",
            render: (val) => <span>{val?.[0]}</span>,
        },
        {
            title: "Date Submitted",
            dataIndex: "onboardingdate",
            key: "onboardingdate",
            className: "normal-column",
            render: (val) => (val ? moment(val).format("DD-MM-YYYY") : "-"),
        },
    ];

    const [form] = Form.useForm();

    const handleFormChange = () => {
        // local form state is sufficient; no redux filter dispatch needed
    };

    const handleCountryChange = (value) => {
        setSelectedManagingOffice(value);
    };

    const { getOnboardingReport } = queryKeys;

    const isInitialMount = useRef(true);
    const previousCountry = useRef(selectedManagingOffice);

    const { key, url } = getOnboardingReport(clientId, selectedManagingOffice);

    const {
        data,
        lastElementRef,
        isLoading,
        refetch,
    } = useScrollFetch(key, {
        endPoint: url,
        queryParam: "LastEvaluatedKey",
        extra: {
            clientId: clientId,
            country: selectedManagingOffice,
        },
        enabled: !!lastEvaluatedKey,
    });

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            previousCountry.current = selectedManagingOffice;
            return;
        }

        if (previousCountry.current !== selectedManagingOffice) {
            previousCountry.current = selectedManagingOffice;
            refetch();
        }
    }, [selectedManagingOffice, refetch]);

    const dataList = useMemo(() => {
        setLastEvaluatedKey(data?.pages?.[data.pages.length - 1]?.response?.LastEvaluatedKey);
        if (data) {
            return data?.pages?.reduce((acc, page) => {
                if (page?.response === undefined) return acc;
                return [...acc, ...page?.response];
            }, []);
        }
        return [];
    }, [data]);

    const userOptions = dataList?.map((user) => ({
        label: user?.fullname,
        value: user?.userid,
    }));

    const managingOfficeOptions = [
        { label: "Kenya", value: "Kenya" },
        { label: "Uganda", value: "Uganda" },
        { label: "Tanzania", value: "Tanzania" },
    ];

    const statusOptions = [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
    ];

    const getFilteredData = () => {
        const name = form.getFieldValue("name") || [];
        const status = form.getFieldValue("status") || [];

        return dataList?.filter((item) => {
            const nameMatch = name.length > 0 ? name.includes(item.userid) : true;
            const statusMatch =
                status.length > 0
                    ? status.includes(item?.onBoardingFormStatus === "0" ? "yes" : "no")
                    : true;
            return nameMatch && statusMatch;
        });
    };

    const xport = React.useCallback(() => {
        const elt = document.getElementById("Table");
        const wb = utils.table_to_book(elt);
        writeFileXLSX(wb, "Onboarding Report.csv");
    });

    return (
        <OnboardingReportWrap>
            <Row>
                <Col sm={24}>
                    <div className="d-flex justify-content-between">
                        <Title>Onboarding Report</Title>
                        <ButtonOutlined
                            className="text-end"
                            disabled={!getFilteredData().length}
                            onClick={xport}
                        >
                            Export CSV file
                        </ButtonOutlined>
                    </div>
                </Col>
                <Col sm={24}>
                    <Form
                        form={form}
                        layout="vertical"
                        className="mt-2"
                        onValuesChange={handleFormChange}
                    >
                        <Row gutter={12} className="g-3 mb-2">
                            <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                                <Form.Item name="name">
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        placeholder="Name"
                                        options={userOptions}
                                        optionFilterProp="label"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                                <Form.Item name="status">
                                    <Select
                                        mode="multiple"
                                        placeholder="Submission Status"
                                        options={statusOptions}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                                <Form.Item name="managingoffice">
                                    <Select
                                        showSearch
                                        placeholder="Select Country"
                                        options={managingOfficeOptions}
                                        optionFilterProp="label"
                                        defaultValue="Kenya"
                                        onChange={handleCountryChange}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
            <Table
                dataSource={getFilteredData() ?? []}
                columns={columns}
                pagination={false}
                loading={isLoading}
                onRow={(record, index) => ({
                    ref: index === dataList?.length - 20 ? lastElementRef : null,
                })}
                id="Table"
            />
        </OnboardingReportWrap>
    );
}

export default OnboardingReport;

const OnboardingReportWrap = styled.div`
  .text-end {
    background-color: #fff;
    color: #000;
    border: 1px solid #000;
    :hover {
        border: 1px solid #000;
        background: #fff;
        color: #000;
    }
  }
`;